/* eslint-disable */
// /src/pages/MyAccountPage.jsx
import React, { useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useMember } from "../context/MemberContext";
import { useDriverStatusContext } from "context/DriverStatusContext";
import { clearAll } from "../services/storage";
import { db } from "../services/firebase";
import {
  collection,
  deleteDoc,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";
import PageTopBar from "components/PageTopBar";

/* ===== 세션 유틸 ===== */
function hardClearSession() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem("dhm.session");
    window.localStorage.removeItem("dhm.boundName");
    window.sessionStorage.clear();
  } catch (e) {
    console.error("[logout] failed to clear web session", e);
  }
}

/* ===== 스타일 ===== */
const Wrap = styled.div`
  min-height: 100vh;
  background: #f7faff;
  display: flex;
  flex-direction: column;
`;

const Body = styled.div`
  flex: 1;
  padding: 16px;
  max-width: 880px;
  width: 100%;
  margin: 0 auto;
`;

const Card = styled.section`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 1px 10px rgba(0, 0, 0, 0.06);
  padding: 6px 16px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 15px;
  padding: 16px 0;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  color: #9ca3af;

  &:last-child {
    border-bottom: none;
  }
  &:active {
    opacity: 0.7;
  }
`;

/* 모달 오버레이 */
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: grid;
  place-items: center;
  z-index: 50;
`;

const ModalBox = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 24px 20px 20px;
  width: min(340px, 90vw);
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.35);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  text-align: center;
`;

const ModalDesc = styled.p`
  margin: 0;
  font-size: 14px;
  color: #4b5563;
  text-align: center;
  line-height: 1.6;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`;

const ModalBtn = styled.button`
  flex: 1;
  height: 44px;
  border-radius: 10px;
  border: ${(p) => (p.$outline ? "1px solid #d1d5db" : "0")};
  background: ${(p) => p.$bg || "#ffffff"};
  color: ${(p) => p.$color || "#374151"};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  &:active {
    transform: translateY(1px);
  }
`;

/* 회원탈퇴 프로그레스 */
const ProgressWrap = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
`;

const ProgressBar = styled.div`
  height: 100%;
  border-radius: 999px;
  background: #dc2626;
  width: ${(p) => `${p.$percent || 0}%`};
  transition: width 0.12s linear;
`;

export default function MyAccountPage() {
  const navigate = useNavigate();

  const { username } = useDriverStatusContext();

  // 로그아웃 모달
  const [logoutModal, setLogoutModal] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  // 회원탈퇴 1차 확인 모달
  const [withdrawConfirm, setWithdrawConfirm] = useState(false);
  // 회원탈퇴 진행 모달
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawProgress, setWithdrawProgress] = useState(0);
  const [withdrawMessage, setWithdrawMessage] = useState("회원탈퇴중입니다...");
  const [withdrawError, setWithdrawError] = useState("");
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);

  /* 로그아웃 처리 */
  const doLogout = async () => {
    try {
      setLoggingOut(true);
      hardClearSession();
      clearAll();
      if (typeof window !== "undefined") {
        window.location.href = "/auth";
        return;
      }
    } catch (e) {
      console.error(e);
      alert("로그아웃 중 문제가 발생했습니다.");
    } finally {
      setLoggingOut(false);
    }
  };

  /* 회원탈퇴 처리 */
  const doWithdraw = async () => {
    if (withdrawing) return;
    setWithdrawConfirm(false);
    setWithdrawError("");
    setWithdrawMessage("회원탈퇴중입니다...");
    setWithdrawProgress(0);
    setWithdrawModalOpen(true);
    setWithdrawing(true);

    try {
      if (!username) {
        throw new Error("회원 정보를 찾을 수 없습니다. 다시 로그인 후 시도해 주세요.");
      }

      const col = collection(db, "members");
      const q = query(col, where("username", "==", username), limit(1));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const ref = snap.docs[0].ref;
        await deleteDoc(ref);
      }

      const steps = 50;
      for (let i = 1; i <= steps; i += 1) {
        setWithdrawProgress((i / steps) * 100);
        await new Promise((res) => setTimeout(res, 100));
      }

      setWithdrawProgress(100);
      setWithdrawMessage("삭제 되었습니다.");
      setWithdrawError("");

      setTimeout(() => {
        try {
          hardClearSession();
          clearAll();
        } catch (e) {
          console.error(e);
        }
        if (typeof window !== "undefined") {
          window.location.href = "/auth";
        }
      }, 800);
    } catch (e) {
      console.error("[withdraw] error", e);
      setWithdrawError(
        e?.message || "회원탈퇴 처리 중 오류가 발생했습니다. 다시 시도해 주세요."
      );
      setWithdrawMessage("회원탈퇴 중 오류가 발생했습니다.");
    } finally {
      setWithdrawing(false);
    }
  };

  return (
    <Wrap>
      <PageTopBar variant="back" label="My Account" />

      <Body>
        <Card>
          <Row onClick={() => setLogoutModal(true)}>
            <span>로그아웃</span>
          </Row>
          <Row onClick={() => setWithdrawConfirm(true)}>
            <span>회원탈퇴</span>
          </Row>
        </Card>
      </Body>

      {/* 로그아웃 확인 모달 */}
      {logoutModal && (
        <Backdrop onClick={() => !loggingOut && setLogoutModal(false)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalTitle>로그아웃</ModalTitle>
            <ModalDesc>로그아웃을 정말로 진행하시겠습니까?</ModalDesc>
            <ModalActions>
              <ModalBtn
                $outline
                onClick={() => setLogoutModal(false)}
                disabled={loggingOut}
              >
                취소
              </ModalBtn>
              <ModalBtn
                $bg="#111827"
                $color="#fff"
                onClick={doLogout}
                disabled={loggingOut}
              >
                {loggingOut ? "처리 중..." : "확인"}
              </ModalBtn>
            </ModalActions>
          </ModalBox>
        </Backdrop>
      )}

      {/* 회원탈퇴 1차 확인 모달 */}
      {withdrawConfirm && (
        <Backdrop onClick={() => setWithdrawConfirm(false)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalTitle>회원탈퇴</ModalTitle>
            <ModalDesc>
              회원탈퇴 시 계정 정보는 복구되지 않습니다.
              <br />
              계속 진행하시겠습니까?
            </ModalDesc>
            <ModalActions>
              <ModalBtn $outline onClick={() => setWithdrawConfirm(false)}>
                탈퇴 취소
              </ModalBtn>
              <ModalBtn $bg="#dc2626" $color="#fff" onClick={doWithdraw}>
                최종 탈퇴
              </ModalBtn>
            </ModalActions>
          </ModalBox>
        </Backdrop>
      )}

      {/* 회원탈퇴 진행 모달 */}
      {withdrawModalOpen && (
        <Backdrop onClick={() => !withdrawing && setWithdrawModalOpen(false)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalTitle>회원탈퇴 진행 중</ModalTitle>
            <ModalDesc>
              {withdrawMessage}
              <br />
              회원 정보를 안전하게 삭제하고 있습니다.
            </ModalDesc>
            <ProgressWrap>
              <ProgressBar $percent={withdrawProgress} />
            </ProgressWrap>
            {withdrawError && (
              <div style={{ fontSize: 12, color: "#b91c1c", whiteSpace: "pre-line" }}>
                {withdrawError}
              </div>
            )}
            {!withdrawing && (
              <ModalActions>
                <ModalBtn $outline onClick={() => setWithdrawModalOpen(false)}>
                  닫기
                </ModalBtn>
              </ModalActions>
            )}
          </ModalBox>
        </Backdrop>
      )}
    </Wrap>
  );
}
